import React, { useState, useEffect } from 'react';
import {
  Card, Tabs, Tag, Button, Modal, Form, Select, Input, Timeline, Upload,
  Table, Space, Typography, Badge, Descriptions, Popconfirm, message,
  Row, Col, Statistic, Divider, Steps, Empty, Tooltip, Result, Alert
} from 'antd';
import {
  AppstoreOutlined, InfoCircleOutlined, BranchesOutlined,
  TeamOutlined, GlobalOutlined, CloudUploadOutlined,
  CheckOutlined, CloseOutlined, RollbackOutlined,
  PlusOutlined, EditOutlined, DeleteOutlined,
  QrcodeOutlined, ClockCircleOutlined, SafetyOutlined,
  LinkOutlined, UserOutlined, CodeOutlined,
  ArrowLeftOutlined, ReloadOutlined, EyeOutlined,
  SettingOutlined, FileTextOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const versionStatusMap = {
  developing: { label: '开发中', color: 'blue', dotColor: '#1890ff' },
  auditing: { label: '审核中', color: 'orange', dotColor: '#fa8c16' },
  approved: { label: '已通过', color: 'cyan', dotColor: '#13c2c2' },
  rejected: { label: '已拒绝', color: 'red', dotColor: '#f5222d' },
  published: { label: '已发布', color: 'green', dotColor: '#52c41a' },
  rolled_back: { label: '已回滚', color: 'default', dotColor: '#8c8c8c' },
};

const versionTypeMap = {
  develop: { label: '开发版', color: 'blue' },
  trial: { label: '体验版', color: 'orange' },
  release: { label: '正式版', color: 'green' },
};

const memberRoleMap = {
  admin: { label: '管理员', color: 'red' },
  developer: { label: '开发者', color: 'blue' },
  tester: { label: '体验者', color: 'orange' },
};

const domainTypeMap = {
  request: { label: 'request 合法域名', color: 'blue' },
  socket: { label: 'socket 合法域名', color: 'purple' },
  uploadFile: { label: 'uploadFile 合法域名', color: 'green' },
  downloadFile: { label: 'downloadFile 合法域名', color: 'orange' },
};

function MiniProgramDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [versions, setVersions] = useState([]);
  const [members, setMembers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('basic');
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewModal, setPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [memberModal, setMemberModal] = useState(false);
  const [domainModal, setDomainModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editingDomain, setEditingDomain] = useState(null);
  const [auditModal, setAuditModal] = useState(false);
  const [auditAction, setAuditAction] = useState(null);
  const [auditReason, setAuditReason] = useState('');
  const [uploadForm] = Form.useForm();
  const [memberForm] = Form.useForm();
  const [domainForm] = Form.useForm();

  const loadProgram = async () => {
    try {
      const data = await api.get(`/admin/mini-programs/${id}`);
      setProgram(data);
    } catch (e) {
      message.error('加载小程序信息失败');
    }
  };

  const loadVersions = async () => {
    try {
      const { list } = await api.get(`/admin/mini-programs/${id}/versions`, { params: { limit: 50 } });
      setVersions(list || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadMembers = async () => {
    try {
      const { list } = await api.get(`/admin/mini-programs/${id}/members`, { params: { limit: 50 } });
      setMembers(list || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadDomains = async () => {
    try {
      const { list } = await api.get(`/admin/mini-programs/${id}/domains`, { params: { limit: 50 } });
      setDomains(list || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadProgram(), loadVersions(), loadMembers(), loadDomains()]);
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, [id]);

  // 版本操作
  const handleUploadCode = async () => {
    try {
      const values = await uploadForm.validateFields();
      if (!uploadFile) {
        message.error('请选择代码包 zip 文件');
        return;
      }
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('version', values.version_number);
      formData.append('type', values.type);
      formData.append('changelog', values.changelog || '');
      await api.post(`/admin/mini-programs/${id}/versions`, formData);
      message.success('代码包上传成功' + (uploadFile ? '' : ''));
      setUploadModal(false);
      uploadForm.resetFields();
      setUploadFile(null);
      loadVersions();
    } catch (e) {
      message.error(e.errorFields ? '请填写完整' : (e.error || '上传失败'));
    }
  };

  // 预览/运行小程序（在 iframe 中加载运行时）
  const handlePreview = async (versionId) => {
    try {
      setPreviewLoading(true);
      setPreviewModal(true);
      const data = await api.get(`/admin/mini-review/preview/${versionId}`);
      if (data.previewUrl) {
        setPreviewUrl(data.previewUrl);
      } else {
        message.error('无法生成预览地址: ' + (data.format || ''));
      }
    } catch (e) {
      message.error(e.error || '预览失败');
      setPreviewModal(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmitAudit = async (versionId) => {
    try {
      await api.post(`/admin/mini-programs/${id}/submit-audit`, { versionId });
      message.success('已提交审核');
      loadVersions();
    } catch (e) {
      message.error('提交审核失败');
    }
  };

  const handleAudit = async (versionId, action) => {
    try {
      if (action === 'approve') {
        await api.post(`/admin/mini-programs/${id}/approve`, { versionId, reason: auditReason });
      } else {
        await api.post(`/admin/mini-programs/${id}/reject`, { versionId, reason: auditReason });
      }
      message.success(action === 'approve' ? '审核通过' : '已拒绝');
      setAuditModal(false);
      setAuditReason('');
      loadVersions();
    } catch (e) {
      message.error('操作失败');
    }
  };

  const handlePublish = async (versionId) => {
    try {
      await api.post(`/admin/mini-programs/${id}/publish`, { versionId });
      message.success('发布成功');
      loadVersions();
      loadProgram();
    } catch (e) {
      message.error('发布失败');
    }
  };

  const handleRollback = async (versionId) => {
    try {
      await api.post(`/admin/mini-programs/${id}/rollback`, { versionId });
      message.success('回滚成功');
      loadVersions();
      loadProgram();
    } catch (e) {
      message.error('回滚失败');
    }
  };

  // 成员操作
  const handleSaveMember = async () => {
    try {
      const values = await memberForm.validateFields();
      if (editingMember) {
        await api.put(`/admin/mini-programs/${id}/members/${editingMember.id}`, values);
        message.success('成员更新成功');
      } else {
        await api.post(`/admin/mini-programs/${id}/members`, values);
        message.success('成员添加成功');
      }
      setMemberModal(false);
      setEditingMember(null);
      memberForm.resetFields();
      loadMembers();
    } catch (e) {
      message.error(e.errorFields ? '请填写完整' : '操作失败');
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await api.delete(`/admin/mini-programs/${id}/members/${memberId}`);
      message.success('成员已移除');
      loadMembers();
    } catch (e) {
      message.error('移除失败');
    }
  };

  const handleEditMember = (record) => {
    setEditingMember(record);
    memberForm.setFieldsValue(record);
    setMemberModal(true);
  };

  // 域名操作
  const handleSaveDomain = async () => {
    try {
      const values = await domainForm.validateFields();
      if (editingDomain) {
        await api.put(`/admin/mini-programs/${id}/domains/${editingDomain.id}`, values);
        message.success('域名更新成功');
      } else {
        await api.post(`/admin/mini-programs/${id}/domains`, values);
        message.success('域名添加成功');
      }
      setDomainModal(false);
      setEditingDomain(null);
      domainForm.resetFields();
      loadDomains();
    } catch (e) {
      message.error(e.errorFields ? '请填写完整' : '操作失败');
    }
  };

  const handleRemoveDomain = async (domainId) => {
    try {
      await api.delete(`/admin/mini-programs/${id}/domains/${domainId}`);
      message.success('域名已移除');
      loadDomains();
    } catch (e) {
      message.error('移除失败');
    }
  };

  const handleEditDomain = (record) => {
    setEditingDomain(record);
    domainForm.setFieldsValue(record);
    setDomainModal(true);
  };

  // 获取当前版本状态流程
  const getCurrentVersionStatus = () => {
    const latestVersion = versions[0];
    if (!latestVersion) return null;
    return latestVersion.status;
  };

  // 版本管理 Tab
  const renderVersionManagement = () => {
    const currentStatus = getCurrentVersionStatus();
    const latestVersion = versions[0];

    const stepsItems = [
      { title: '开发', description: '上传代码', icon: <CodeOutlined /> },
      { title: '提交审核', description: '等待审核', icon: <FileTextOutlined /> },
      { title: '审核', description: '审核中', icon: <SafetyOutlined /> },
      { title: '发布', description: '上线运行', icon: <CloudUploadOutlined /> },
    ];

    const getStepStatus = () => {
      if (!currentStatus) return 0;
      const statusStep = {
        developing: 0,
        auditing: 1,
        approved: 2,
        published: 3,
        rejected: 2,
      };
      return statusStep[currentStatus] ?? 0;
    };

    return (
      <div>
        {/* 当前版本状态流程 */}
        <Card
          size="small"
          title={<span><BranchesOutlined /> 版本流程</span>}
          style={{ marginBottom: 16 }}
          extra={
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={() => setUploadModal(true)}
              style={{ background: '#07C160', borderColor: '#07C160' }}
            >
              上传代码
            </Button>
          }
        >
          <Steps
            current={getStepStatus()}
            items={stepsItems}
            style={{ marginBottom: 16 }}
          />
          {latestVersion && (
            <Alert
              message={`当前版本: ${latestVersion.version_number} (${versionTypeMap[latestVersion.type]?.label}) - ${versionStatusMap[latestVersion.status]?.label}`}
              type={latestVersion.status === 'published' ? 'success' : latestVersion.status === 'rejected' ? 'error' : 'info'}
              showIcon
              action={
                <Space>
                  <Button size="small" icon={<EyeOutlined />} onClick={() => handlePreview(latestVersion.id)}>
                    预览/运行
                  </Button>
                  {latestVersion.status === 'developing' && (
                    <Button size="small" onClick={() => handleSubmitAudit(latestVersion.id)}>
                      提交审核
                    </Button>
                  )}
                  {latestVersion.status === 'auditing' && (
                    <>
                      <Button size="small" type="primary" onClick={() => { setAuditAction('approve'); setAuditModal(true); }}>
                        审核通过
                      </Button>
                      <Button size="small" danger onClick={() => { setAuditAction('reject'); setAuditModal(true); }}>
                        审核拒绝
                      </Button>
                    </>
                  )}
                  {latestVersion.status === 'approved' && (
                    <Button size="small" type="primary" icon={<CloudUploadOutlined />} onClick={() => handlePublish(latestVersion.id)} style={{ background: '#07C160', borderColor: '#07C160' }}>
                      发布上线
                    </Button>
                  )}
                  {latestVersion.status === 'published' && versions.length > 1 && (
                    <Popconfirm title="确定要回滚到上一个版本吗?" onConfirm={() => handleRollback(latestVersion.id)}>
                      <Button size="small" icon={<RollbackOutlined />}>回滚</Button>
                    </Popconfirm>
                  )}
                </Space>
              }
            />
          )}
        </Card>

        {/* 版本时间线 */}
        <Card size="small" title={<span><ClockCircleOutlined /> 版本历史</span>}>
          {versions.length === 0 ? (
            <Empty description="暂无版本记录" />
          ) : (
            <Timeline
              mode="left"
              items={versions.map((v) => ({
                color: versionStatusMap[v.status]?.dotColor || '#1890ff',
                label: <Text type="secondary" style={{ fontSize: 12 }}>{v.created_at}</Text>,
                children: (
                  <Card size="small" style={{ background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Space>
                        <Tag color={versionTypeMap[v.type]?.color}>{versionTypeMap[v.type]?.label}</Tag>
                        <Text strong>{v.version_number}</Text>
                        <Tag color={versionStatusMap[v.status]?.color}>{versionStatusMap[v.status]?.label}</Tag>
                      </Space>
                      <Space>
                        <Button size="small" icon={<EyeOutlined />} onClick={() => handlePreview(v.id)}>预览/运行</Button>
                        {v.status === 'developing' && (
                          <Button size="small" onClick={() => handleSubmitAudit(v.id)}>提交审核</Button>
                        )}
                        {v.status === 'auditing' && (
                          <>
                            <Button size="small" type="primary" onClick={() => { setAuditAction('approve'); setAuditModal(true); }}>通过</Button>
                            <Button size="small" danger onClick={() => { setAuditAction('reject'); setAuditModal(true); }}>拒绝</Button>
                          </>
                        )}
                        {v.status === 'approved' && (
                          <Button size="small" type="primary" onClick={() => handlePublish(v.id)} style={{ background: '#07C160', borderColor: '#07C160' }}>发布</Button>
                        )}
                        {v.status === 'published' && (
                          <Popconfirm title="确定回滚?" onConfirm={() => handleRollback(v.id)}>
                            <Button size="small" icon={<RollbackOutlined />}>回滚</Button>
                          </Popconfirm>
                        )}
                      </Space>
                    </div>
                    {v.changelog && (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary">变更说明: </Text>
                        <Text>{v.changelog}</Text>
                      </div>
                    )}
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        代码大小: {v.code_size ? `${(v.code_size / 1024).toFixed(1)}KB` : '-'}
                        {v.code_url && <> | <a href={v.code_url} target="_blank" rel="noopener noreferrer"><LinkOutlined /> 下载代码包</a></>}
                      </Text>
                    </div>
                  </Card>
                ),
              }))}
            />
          )}
        </Card>
      </div>
    );
  };

  // 成员管理 Tab
  const renderMemberManagement = () => {
    const memberColumns = [
      { title: 'ID', dataIndex: 'id', width: 60 },
      {
        title: '成员', width: 180,
        render: (_, r) => (
          <Space>
            <UserOutlined />
            <Text strong>{r.nickname || r.username}</Text>
          </Space>
        ),
      },
      { title: '用户ID', dataIndex: 'user_id', width: 100 },
      {
        title: '角色', width: 100,
        render: (_, r) => <Tag color={memberRoleMap[r.role]?.color}>{memberRoleMap[r.role]?.label}</Tag>,
      },
      {
        title: '权限', width: 200,
        render: (_, r) => {
          const perms = r.permissions ? (Array.isArray(r.permissions) ? r.permissions : JSON.parse(r.permissions || '[]')) : [];
          return perms.length > 0 ? (
            <Space wrap>
              {perms.map((p, i) => <Tag key={i} color="geekblue">{p}</Tag>)}
            </Space>
          ) : '-';
        },
      },
      { title: '添加时间', dataIndex: 'created_at', width: 160 },
      {
        title: '操作', width: 120,
        render: (_, r) => (
          <Space size="small">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEditMember(r)}>编辑</Button>
            <Popconfirm title="确定移除该成员?" onConfirm={() => handleRemoveMember(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ];

    return (
      <div>
        <Card
          size="small"
          title={<span><TeamOutlined /> 成员列表</span>}
          style={{ marginBottom: 16 }}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { setEditingMember(null); memberForm.resetFields(); setMemberModal(true); }}
              style={{ background: '#07C160', borderColor: '#07C160' }}
            >
              添加成员
            </Button>
          }
        >
          <Table
            columns={memberColumns}
            dataSource={members}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10 }}
          />
        </Card>

        <Card size="small" style={{ background: '#fffbe6', border: '1px solid #ffe58f' }}>
          <Text type="secondary">
            <SafetyOutlined /> 权限说明：管理员拥有全部权限，开发者可上传代码和提交审核，体验者仅可体验开发版/体验版。
          </Text>
        </Card>
      </div>
    );
  };

  // 域名配置 Tab
  const renderDomainConfiguration = () => {
    const domainColumns = [
      { title: 'ID', dataIndex: 'id', width: 60 },
      {
        title: '域名类型', width: 160,
        render: (_, r) => <Tag color={domainTypeMap[r.type]?.color}>{domainTypeMap[r.type]?.label}</Tag>,
      },
      {
        title: '域名', width: 300,
        render: (_, r) => (
          <Text code style={{ wordBreak: 'break-all' }}>{r.domain}</Text>
        ),
      },
      { title: '备注', dataIndex: 'remark', ellipsis: true },
      { title: '添加时间', dataIndex: 'created_at', width: 160 },
      {
        title: '操作', width: 120,
        render: (_, r) => (
          <Space size="small">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEditDomain(r)}>编辑</Button>
            <Popconfirm title="确定删除该域名?" onConfirm={() => handleRemoveDomain(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ];

    // 按类型分组统计
    const domainStats = {
      request: domains.filter(d => d.type === 'request').length,
      socket: domains.filter(d => d.type === 'socket').length,
      uploadFile: domains.filter(d => d.type === 'uploadFile').length,
      downloadFile: domains.filter(d => d.type === 'downloadFile').length,
    };

    return (
      <div>
        {/* 域名统计 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          {Object.entries(domainTypeMap).map(([key, val]) => (
            <Col span={6} key={key}>
              <Card size="small">
                <Statistic
                  title={val.label}
                  value={domainStats[key] || 0}
                  prefix={<GlobalOutlined style={{ color: val.color }} />}
                  suffix="个"
                />
              </Card>
            </Col>
          ))}
        </Row>

        <Card
          size="small"
          title={<span><GlobalOutlined /> 域名列表</span>}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { setEditingDomain(null); domainForm.resetFields(); setDomainModal(true); }}
              style={{ background: '#07C160', borderColor: '#07C160' }}
            >
              添加域名
            </Button>
          }
        >
          <Table
            columns={domainColumns}
            dataSource={domains}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10 }}
          />
        </Card>

        <Card size="small" style={{ marginTop: 16, background: '#e6f7ff', border: '1px solid #91d5ff' }}>
          <Text type="secondary">
            <InfoCircleOutlined /> 提示：域名需要备案，且只支持 HTTPS (wss) 协议。配置后需重新发布小程序生效。
          </Text>
        </Card>
      </div>
    );
  };

  // 基本信息 Tab
  const renderBasicInfo = () => {
    if (!program) return <Empty description="暂无数据" />;

    return (
      <div>
        <Card
          size="small"
          title={<span><InfoCircleOutlined /> 基本信息</span>}
          style={{ marginBottom: 16 }}
          extra={
            <Button icon={<EditOutlined />}>编辑</Button>
          }
        >
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="小程序名称">
              <Space>
                <AppstoreOutlined style={{ color: '#07C160' }} />
                <Text strong>{program.name}</Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="AppID">
              <Tag color="geekblue" style={{ fontFamily: 'monospace' }}>{program.app_id || '-'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="AppSecret">
              {program.app_secret ? (
                <Space>
                  <Text code>******</Text>
                  <Tooltip title="点击复制">
                    <Button
                      size="small"
                      icon={<LinkOutlined />}
                      onClick={() => {
                        navigator.clipboard?.writeText(program.app_secret);
                        message.success('已复制');
                      }}
                    />
                  </Tooltip>
                </Space>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="分类">
              {program.category_name ? <Tag color="purple">{program.category_name}</Tag> : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={statusMap[program.status]?.color}>{statusMap[program.status]?.label || program.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="当前版本">
              {program.current_version ? (
                <Space>
                  <BranchesOutlined style={{ color: '#1890ff' }} />
                  <Text>{program.current_version}</Text>
                </Space>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="二维码">
              {program.qr_code ? (
                <Tag color="green" icon={<QrcodeOutlined />}>二维码已生成</Tag>
              ) : (
                <Tag>未生成</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="过期时间">
              {program.expire_time || '永不过期'}
            </Descriptions.Item>
            <Descriptions.Item label="创建者">{program.creator || '-'}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{program.created_at || '-'}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{program.updated_at || '-'}</Descriptions.Item>
            <Descriptions.Item label="描述" span={2}>
              <Paragraph style={{ marginBottom: 0 }}>{program.description || '暂无描述'}</Paragraph>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 快捷操作 */}
        <Card size="small" title={<span><SettingOutlined /> 快捷操作</span>}>
          <Row gutter={16}>
            <Col span={6}>
              <Card.Grid style={{ width: '100%', textAlign: 'center', padding: 16 }}>
                <Button type="link" icon={<CloudUploadOutlined />} onClick={() => setUploadModal(true)} style={{ fontSize: 16 }}>
                  上传代码
                </Button>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>上传开发版/体验版代码</Text>
              </Card.Grid>
            </Col>
            <Col span={6}>
              <Card.Grid style={{ width: '100%', textAlign: 'center', padding: 16 }}>
                <Button type="link" icon={<QrcodeOutlined />} style={{ fontSize: 16 }}>
                  获取二维码
                </Button>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>生成小程序体验二维码</Text>
              </Card.Grid>
            </Col>
            <Col span={6}>
              <Card.Grid style={{ width: '100%', textAlign: 'center', padding: 16 }}>
                <Button type="link" icon={<TeamOutlined />} onClick={() => setActiveTab('members')} style={{ fontSize: 16 }}>
                  成员管理
                </Button>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>管理开发者和体验者</Text>
              </Card.Grid>
            </Col>
            <Col span={6}>
              <Card.Grid style={{ width: '100%', textAlign: 'center', padding: 16 }}>
                <Button type="link" icon={<GlobalOutlined />} onClick={() => setActiveTab('domains')} style={{ fontSize: 16 }}>
                  域名配置
                </Button>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>配置服务器域名白名单</Text>
              </Card.Grid>
            </Col>
          </Row>
        </Card>
      </div>
    );
  };

  const statusMap = {
    developing: { label: '开发中', color: 'blue' },
    auditing: { label: '审核中', color: 'orange' },
    approved: { label: '已通过', color: 'cyan' },
    rejected: { label: '已拒绝', color: 'red' },
    published: { label: '已发布', color: 'green' },
    disabled: { label: '已禁用', color: 'default' },
  };

  const tabItems = [
    {
      key: 'basic',
      label: <span><InfoCircleOutlined /> 基本信息</span>,
      children: renderBasicInfo(),
    },
    {
      key: 'versions',
      label: <span><BranchesOutlined /> 版本管理 <Badge count={versions.length} style={{ backgroundColor: '#1890ff' }} /></span>,
      children: renderVersionManagement(),
    },
    {
      key: 'members',
      label: <span><TeamOutlined /> 成员管理 <Badge count={members.length} style={{ backgroundColor: '#52c41a' }} /></span>,
      children: renderMemberManagement(),
    },
    {
      key: 'domains',
      label: <span><GlobalOutlined /> 域名配置 <Badge count={domains.length} style={{ backgroundColor: '#722ed1' }} /></span>,
      children: renderDomainConfiguration(),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <ReloadOutlined spin style={{ fontSize: 32, color: '#1890ff' }} />
        <div style={{ marginTop: 16 }}>加载中...</div>
      </div>
    );
  }

  return (
    <>
      {/* 顶部导航 */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/mini-programs')}>
            返回列表
          </Button>
          <Divider type="vertical" />
          <Title level={4} style={{ margin: 0 }}>
            <AppstoreOutlined style={{ color: '#07C160' }} /> {program?.name || '加载中...'}
          </Title>
          {program?.status && (
            <Tag color={statusMap[program.status]?.color}>{statusMap[program.status]?.label}</Tag>
          )}
        </Space>
      </div>

      {/* 主内容 */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="card"
        />
      </Card>

      {/* 上传代码弹窗 */}
      <Modal
        title={<span><CloudUploadOutlined /> 上传代码</span>}
        open={uploadModal}
        onOk={handleUploadCode}
        onCancel={() => { setUploadModal(false); uploadForm.resetFields(); }}
        width={600}
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item
            name="type"
            label="版本类型"
            rules={[{ required: true, message: '请选择版本类型' }]}
            initialValue="develop"
          >
            <Select
              options={[
                { value: 'develop', label: '开发版 (develop)' },
                { value: 'trial', label: '体验版 (trial)' },
                { value: 'release', label: '正式版 (release)' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="version_number"
            label="版本号"
            rules={[{ required: true, message: '请输入版本号' }]}
            extra="格式如: 1.0.0, 1.2.1"
          >
            <Input placeholder="1.0.0" />
          </Form.Item>
          <Form.Item
            name="changelog"
            label="版本变更说明"
            rules={[{ required: true, message: '请输入变更说明' }]}
          >
            <TextArea rows={3} placeholder="描述本次更新的内容..." />
          </Form.Item>
          <Form.Item label="代码包 (.zip)" required>
            <Upload.Dragger
              accept=".zip"
              maxCount={1}
              beforeUpload={(file) => { setUploadFile(file); return false; }}
              onRemove={() => setUploadFile(null)}
              fileList={uploadFile ? [{ uid: '-1', name: uploadFile.name, status: 'done' }] : []}
            >
              <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
              <p className="ant-upload-text">点击或拖拽小程序代码包 (.zip)</p>
              <p className="ant-upload-hint">支持微信格式（app.json + pages/*）或 H5 网页包（index.html）</p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>

      {/* 预览/运行小程序 */}
      <Modal
        title={<span><EyeOutlined /> 预览/运行小程序</span>}
        open={previewModal}
        onCancel={() => { setPreviewModal(false); setPreviewUrl(''); }}
        footer={null}
        width="92%"
        style={{ maxWidth: 480, top: 24 }}
        styles={{ body: { height: '80vh', padding: 0 } }}
      >
        {previewLoading ? (
          <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin tip="正在准备预览..." />
          </div>
        ) : previewUrl ? (
          <iframe
            src={previewUrl}
            title="mini-program-preview"
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        ) : (
          <div style={{ padding: 24 }}>暂无预览</div>
        )}
      </Modal>

      {/* 成员管理弹窗 */}
      <Modal
        title={editingMember ? '编辑成员' : '添加成员'}
        open={memberModal}
        onOk={handleSaveMember}
        onCancel={() => { setMemberModal(false); setEditingMember(null); memberForm.resetFields(); }}
      >
        <Form form={memberForm} layout="vertical">
          <Form.Item
            name="user_id"
            label="用户ID"
            rules={[{ required: true, message: '请输入用户ID' }]}
          >
            <Input placeholder="输入用户ID" disabled={!!editingMember} />
          </Form.Item>
          <Form.Item
            name="nickname"
            label="成员名称"
          >
            <Input placeholder="成员显示名称" />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
            initialValue="developer"
          >
            <Select
              options={[
                { value: 'admin', label: '管理员' },
                { value: 'developer', label: '开发者' },
                { value: 'tester', label: '体验者' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="permissions"
            label="权限"
            extra="多选权限项"
          >
            <Select
              mode="multiple"
              placeholder="选择权限"
              options={[
                { value: 'upload_code', label: '上传代码' },
                { value: 'submit_audit', label: '提交审核' },
                { value: 'publish', label: '发布上线' },
                { value: 'manage_member', label: '成员管理' },
                { value: 'manage_domain', label: '域名配置' },
                { value: 'view_log', label: '查看日志' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 域名配置弹窗 */}
      <Modal
        title={editingDomain ? '编辑域名' : '添加域名'}
        open={domainModal}
        onOk={handleSaveDomain}
        onCancel={() => { setDomainModal(false); setEditingDomain(null); domainForm.resetFields(); }}
      >
        <Form form={domainForm} layout="vertical">
          <Form.Item
            name="type"
            label="域名类型"
            rules={[{ required: true, message: '请选择域名类型' }]}
          >
            <Select
              options={[
                { value: 'request', label: 'request 合法域名' },
                { value: 'socket', label: 'socket 合法域名' },
                { value: 'uploadFile', label: 'uploadFile 合法域名' },
                { value: 'downloadFile', label: 'downloadFile 合法域名' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="domain"
            label="域名"
            rules={[{ required: true, message: '请输入域名' }]}
            extra="不需要包含协议头，如: api.example.com"
          >
            <Input placeholder="api.example.com" />
          </Form.Item>
          <Form.Item
            name="remark"
            label="备注"
          >
            <Input placeholder="域名用途说明" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 审核弹窗 */}
      <Modal
        title={auditAction === 'approve' ? '审核通过' : '审核拒绝'}
        open={auditModal}
        onOk={() => handleAudit(versions.find(v => v.status === 'auditing')?.id, auditAction)}
        onCancel={() => { setAuditModal(false); setAuditReason(''); }}
        okText={auditAction === 'approve' ? '确认通过' : '确认拒绝'}
        okButtonProps={{ danger: auditAction === 'reject' }}
      >
        <Form layout="vertical">
          <Form.Item label={auditAction === 'approve' ? '通过说明 (可选)' : '拒绝原因'}>
            <TextArea
              rows={3}
              value={auditReason}
              onChange={(e) => setAuditReason(e.target.value)}
              placeholder={auditAction === 'approve' ? '可填写通过说明' : '请填写拒绝原因...'}
            />
          </Form.Item>
          {auditAction === 'reject' && (
            <Alert
              message="拒绝后开发者需要修改代码并重新提交审核"
              type="warning"
              showIcon
            />
          )}
        </Form>
      </Modal>
    </>
  );
}

export default MiniProgramDetailPage;
